import { gql } from 'graphql-request'
import { customEndpoint } from '@directus/sdk'
import { useSuivi } from '~/store/suivi'

export function useMondayAPI () {
  // Escape special characters for GraphQL string literals
  const escapeGraphQLString = (str: string): string => {
    if (typeof str !== 'string') { return str }
    return str
      .replace(/\\/g, '\\\\')
      .replace(/"/g, '\\"')
      .replace(/\n/g, '\\n')
      .replace(/\r/g, '\\r')
      .replace(/\t/g, '\\t')
  }

  const requestItems = async (ids: Array<number>, exclude_nonactive:Boolean = false) => {
    useSuivi().loading = true

    const document = gql`
      {
        items(ids: [${ids}], exclude_nonactive:${exclude_nonactive}) {
                    id
                    name
                    state
                    board{
                        id
                    }
                    assets {
                        id
                        public_url
                    }
                    column_values {
                        id
                        text
                        type
                        value
                        ... on MirrorValue{
                            display_value
                        }
                        ... on BoardRelationValue {
                            display_value
                            linked_item_ids
                        }
                        column {
                            title
                            description
                            id
                        }
                    }
                    subitems {
                        id
                        name
                        board{
                            id
                        }
                        column_values {
                            id
                            text
                            type
                            value
                            column {
                                title
                                description
                                id
                            }
                        }
                    }
                    updates {
                        body
                        id
                        replies {
                            body
                            id
                            created_at
                            creator {
                                name
                                photo_thumb
                                id
                            }
                        }
                    }
                }
            }`

    try {
      const data = await useDirectus().client.request(customEndpoint({
        path: '/monday-api-proxy',
        method: 'POST',
        body: JSON.stringify({
          gql: document
        })
      }))
      useSuivi().loading = false
      return data.body
    } catch (e) {
      useReportBug().errorMessage.value = "⚠️ Oups ! Une erreur inattendue s’est produite lors du chargement des données. Veuillez réessayer dans quelques instants ou n'hésitez pas à nous reporter le problème."
      useReportBug().error.value = 'Erreur lors du chargement des ventes et fonds (mondayApi.ts:requestItems)'
      useReportBug().errorDetail.value = e
      useReportBug().idSuivi.value = useRoute().params.id
      useReportBug().hasError.value = true
    }
  }

  const createUpdate = async (suivi, update, parentId) => {
    const escapedUpdate = escapeGraphQLString(update)
    const document = gql`mutation {
            create_update (item_id: ${suivi.id}, body: "${escapedUpdate}", parent_id :${parentId}) {
                id
            }
        }`

    try {
      return await useDirectus().client.request(customEndpoint({
        path: '/monday-api-proxy',
        method: 'POST',
        body: JSON.stringify({
          gql: document
        })
      }))
    } catch (e) {
      useReportBug().errorMessage.value = "⚠️ Oups ! Une erreur inattendue s’est produite lors de l'envoi de votre message. Veuillez réessayer dans quelques instants ou n'hésitez pas à nous reporter le problème."
      useReportBug().error.value = "Erreur lors de l'envoi d'un message (mondayApi.ts:createUpdate)"
      useReportBug().errorDetail.value = e
      useReportBug().idSuivi.value = useRoute()?.params?.id ?? false
      useReportBug().hasError.value = true
    }
  }

  const createSubItem = async (parentId, name, column_values) => {
    const escapedName = escapeGraphQLString(name)
    const document = gql`
            mutation {
                create_subitem (parent_item_id: ${parentId}, item_name: "${escapedName}") {
                    id
                    board{
                        id
                    }
                    column_values{
                        text
                        id

                    }
                }
            }
        `

    try {
      return await useDirectus().client.request(customEndpoint({
        path: '/monday-api-proxy',
        method: 'POST',
        body: JSON.stringify({
          gql: document
        })
      }))
    } catch (e) {
      useReportBug().errorMessage.value = "⚠️ Oups ! Une erreur inattendue s’est produite de la création du sous-élément. Veuillez réessayer dans quelques instants ou n'hésitez pas à nous reporter le problème."
      useReportBug().error.value = 'Erreur lors du chargement des ventes et fonds (mondayApi.ts:createSubItem)'

      useReportBug().errorDetail.value = e
      useReportBug().idSuivi.value = useRoute().params.id
      useReportBug().hasError.value = true
    }
  }

  const getFacturation = async (suiviId) => {
    const document = gql`{
            items_page_by_column_values (limit: 50, board_id: 3228165537, columns: [{column_id: "text_mkp76zzs", column_values: ["${suiviId}"]}]) {
                items {
                    id
                    name
                    board{
                        id
                    }
                    assets {
                        id
                        public_url
                    }
                    column_values{
                        id
                        text
                        type
                        value
                        ... on MirrorValue{
                            display_value
                        }
                        ... on BoardRelationValue {
                            display_value
                            linked_item_ids
                        }
                        column {
                            title
                            description
                            id
                        }
                    }
                }
            }
        }
        `

    try {
      return await useDirectus().client.request(customEndpoint({
        path: '/monday-api-proxy',
        method: 'POST',
        body: JSON.stringify({
          gql: document
        })
      }))
    } catch (e) {
      useReportBug().errorMessage.value = "⚠️ Oups ! Une erreur inattendue lors du chargement de vos factures. Veuillez réessayer dans quelques instants ou n'hésitez pas à nous reporter le problème."
      useReportBug().error.value = 'Erreur lors du chargement des ventes et fonds (mondayApi.ts:getFacturation)'
      useReportBug().errorDetail.value = e
      useReportBug().idSuivi.value = useRoute().params.id
      useReportBug().hasError.value = true
    }
  }

  const getVentesEtFonds = async (suiviId) => {
    const document = gql`{
            items_page_by_column_values (limit: 50, board_id: 8045015536, columns: [{column_id: "text_mkrwhpgj", column_values: ["${suiviId}"]}]) {
                items {
                    id
                    name
                    board{
                        id
                    }
                    assets {
                        id
                        public_url
                    }
                    column_values{
                        id
                        text
                        type
                        value
                        ... on MirrorValue{
                            display_value
                        }
                        ... on BoardRelationValue {
                            display_value
                            linked_item_ids
                        }
                        column {
                            title
                            description
                            id
                        }
                    }
                    subitems {
                        id
                        name
                        board{
                            id
                        }
                        column_values {
                            id
                            text
                            type
                            value
                            column {
                                title
                                description
                                id
                            }
                        }
                    }
                }
            }
        }
        `
    try {
      return await useDirectus().client.request(customEndpoint({
        path: '/monday-api-proxy',
        method: 'POST',
        body: JSON.stringify({
          gql: document
        })
      }))
    } catch (e) {
      useReportBug().errorMessage.value = "⚠️ Oups ! Une erreur inattendue lors du chargement de vos factures. Veuillez réessayer dans quelques instants ou n'hésitez pas à nous reporter le problème."
      useReportBug().error.value = 'Erreur lors du chargement des ventes et fonds (mondayApi.ts:getVentesEtFonds)'
      useReportBug().errorDetail.value = e
      useReportBug().idSuivi.value = useRoute().params.id
      useReportBug().hasError.value = true
    }
  }

  const changeColumnValue = async (itemId, boardId, columnId, value) => {
    if (typeof value === 'object') {
      return await changeJSONColumnValue(itemId, boardId, columnId, value)
    }

    const escapedValue = escapeGraphQLString(value)
    const document = gql`
            mutation {
                change_simple_column_value(item_id: ${itemId}, board_id: ${boardId}, column_id: "${columnId}", value: "${escapedValue}") {
                    id
                }
            }`

    try {
      return await useDirectus().client.request(customEndpoint({
        path: '/monday-api-proxy',
        method: 'POST',
        body: JSON.stringify({
          gql: document
        })
      }))
    } catch (e) {
      useReportBug().errorMessage.value = "⚠️ Oups ! Une erreur inattendue s’est produite. Veuillez réessayer dans quelques instants ou n'hésitez pas à nous reporter le problème."
      useReportBug().error.value = 'Erreur (mondayApi.ts:changeColumnValue)'
      useReportBug().errorDetail.value = e
      useReportBug().idSuivi.value = useRoute().params.id
      useReportBug().hasError.value = true
    }
  }

  const changeJSONColumnValue = async (itemId, boardId, columnId, value) => {
    const document = gql`
            mutation {
                change_column_value(item_id: ${itemId}, board_id: ${boardId}, column_id: "${columnId}", value: "${stringify(value)}") {
                    id
                }
            }`

    try {
      return await useDirectus().client.request(customEndpoint({
        path: '/monday-api-proxy',
        method: 'POST',
        body: JSON.stringify({
          gql: document
        })
      }))
    } catch (e) {
      useReportBug().errorMessage.value = "⚠️ Oups ! Une erreur inattendue s’est produite. Veuillez réessayer dans quelques instants ou n'hésitez pas à nous reporter le problème."
      useReportBug().error.value = 'Erreur (mondayApi.ts:changeJSONColumnValue)'
      useReportBug().errorDetail.value = e
      useReportBug().idSuivi.value = useRoute().params.id
      useReportBug().hasError.value = true
    }
  }

  const stringify = (object) => {
    if (typeof object === 'object') {
      return JSON.stringify(object).replaceAll('\"', '\\"')
    }
    return object
  }

  // expose managed state as return value
  return { requestItems, createUpdate, createSubItem, changeColumnValue, getFacturation, getVentesEtFonds }
}
